import { Menu } from '@mui/material';
import { Archive, FolderPlus, PencilSimple } from '@phosphor-icons/react';
import React, { useState } from 'react';
import type { CustomColorPalette } from '../../../../theme';
import { SidebarSection } from '../shared/SidebarSection';
import { DeleteMenuItem, StyledMenuItem } from '../sidebarSectionStyles';
import { useDataSidebarContext } from './context';

const contextMenuPaperSx = (
  isDarkMode: boolean,
  palette: CustomColorPalette,
) => ({
  backgroundColor: isDarkMode ? palette.gray[800] : palette.gray[50],
  border: `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[200]}`,
  borderRadius: '8px',
  minWidth: '160px',
  boxShadow: isDarkMode
    ? '0 4px 12px rgba(0,0,0,0.4)'
    : '0 4px 12px rgba(0,0,0,0.1)',
});

interface SectionProps {
  title: string;
  children: React.ReactNode;
  hasBorder?: boolean;
  isAccordion?: boolean;
  defaultExpanded?: boolean;
  isDisabled?: boolean;
  spaceId?: string | null;
  onAddFolder?: () => void;
  onArchive?: () => void;
  onEditSpace?: () => void;
}

export const Section: React.FC<SectionProps> = ({
  title,
  children,
  hasBorder = false,
  isAccordion = false,
  defaultExpanded = false,
  isDisabled = false,
  spaceId = null,
  onAddFolder,
  onArchive,
  onEditSpace,
}) => {
  const { isDarkMode, palette } = useDataSidebarContext();
  const [contextMenuPos, setContextMenuPos] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const hasContextActions = !!onAddFolder || !!onArchive || !!onEditSpace;

  const handleContextMenu = (e: React.MouseEvent) => {
    if (!hasContextActions) return;
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ mouseX: e.clientX, mouseY: e.clientY });
  };

  const handleClose = () => setContextMenuPos(null);

  const contextMenu = hasContextActions ? (
    <Menu
      open={contextMenuPos !== null}
      onClose={handleClose}
      anchorReference='anchorPosition'
      anchorPosition={
        contextMenuPos !== null
          ? { top: contextMenuPos.mouseY, left: contextMenuPos.mouseX }
          : undefined
      }
      slotProps={{ paper: { sx: contextMenuPaperSx(isDarkMode, palette) } }}
    >
      {onAddFolder && (
        <StyledMenuItem
          onClick={() => {
            handleClose();
            onAddFolder();
          }}
        >
          <FolderPlus size={16} />
          Add Folder
        </StyledMenuItem>
      )}
      {onEditSpace && (
        <StyledMenuItem
          onClick={() => {
            handleClose();
            onEditSpace();
          }}
        >
          <PencilSimple size={16} />
          Edit Space
        </StyledMenuItem>
      )}
      {onArchive && !isDisabled && (
        <DeleteMenuItem
          onClick={() => {
            handleClose();
            onArchive();
          }}
        >
          <Archive size={16} />
          Archive
        </DeleteMenuItem>
      )}
    </Menu>
  ) : null;

  return (
    <SidebarSection
      title={title}
      section='data'
      spaceId={spaceId}
      hasBorder={hasBorder}
      isAccordion={isAccordion}
      defaultExpanded={defaultExpanded}
      isDisabled={isDisabled}
      onAddFolder={onAddFolder}
      onContextMenu={handleContextMenu}
      contextMenu={contextMenu}
    >
      {children}
    </SidebarSection>
  );
};
